import { useState, useRef, useEffect, useCallback } from "react";
import { X, CameraOff, RotateCcw } from "lucide-react";
import { createPortal } from "react-dom";
import * as THREE from "three";

export default function ARCameraView({ isOpen, onClose, visualElements = [] }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasContainerRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(false);

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setCameraActive(true);
      setCameraError(false);
    } catch {
      setCameraError(true);
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  }, []);

  useEffect(() => {
    if (isOpen) startCamera();
    return () => stopCamera();
  }, [isOpen, startCamera, stopCamera]);

  // Simple 3D overlay using raw Three.js
  useEffect(() => {
    if (!isOpen || !canvasContainerRef.current || !visualElements.length) return;
    const container = canvasContainerRef.current;
    const w = container.clientWidth;
    const h = container.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, w / h, 0.1, 100);
    camera.position.set(0, 0, 0);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(w, h);
    renderer.setClearColor(0x000000, 0);
    container.innerHTML = "";
    container.appendChild(renderer.domElement);

    const ambient = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambient);
    const point = new THREE.PointLight(0x00F0FF, 1, 10);
    point.position.set(2, 3, 2);
    scene.add(point);

    const cyanColor = new THREE.Color(0x00F0FF);
    visualElements.forEach((el, i) => {
      const scale = 0.35;
      const pos = [el.position[0] * scale - 0.7, el.position[1] * scale - 0.3, el.position[2] * scale - 2];
      if (el.type === "Plane") {
        const mesh = new THREE.Mesh(
          new THREE.PlaneGeometry(0.5, 0.35),
          new THREE.MeshBasicMaterial({ color: cyanColor, wireframe: true, transparent: true, opacity: 0.7, side: THREE.DoubleSide })
        );
        mesh.position.set(...pos);
        mesh.userData = { index: i };
        scene.add(mesh);
      } else {
        const mesh = new THREE.Mesh(
          new THREE.BoxGeometry(0.25, 0.25, 0.25),
          new THREE.MeshBasicMaterial({ color: cyanColor, wireframe: true, transparent: true, opacity: 0.7 })
        );
        mesh.position.set(...pos);
        mesh.userData = { index: i };
        scene.add(mesh);
      }
    });

    const clock = new THREE.Clock();
    let animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      const t = clock.getElapsedTime();
      scene.children.forEach(child => {
        if (child.userData?.index !== undefined) {
          child.position.y += Math.sin(t + child.userData.index) * 0.0005;
          if (child.geometry.type === "BoxGeometry") {
            child.rotation.y = t * 0.4 + child.userData.index;
          }
        }
      });
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(animId);
      renderer.dispose();
      scene.clear();
    };
  }, [isOpen, visualElements]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 99999 }}>
      <div className="absolute inset-0 bg-black" />
      <div className="relative w-full h-full" data-testid="ar-camera-view">
        <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 w-full h-full object-cover" style={{ transform: "scaleX(-1)" }} />

        {cameraActive && (
          <div ref={canvasContainerRef} className="absolute inset-0" style={{ pointerEvents: "none" }} />
        )}

        {cameraError && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#05050A]">
            <div className="text-center">
              <CameraOff className="w-16 h-16 text-[#606070] mx-auto mb-4" />
              <h3 className="font-['Outfit'] text-xl text-white mb-2">Kamera nicht verfuegbar</h3>
              <p className="text-[#A0A0B0] text-sm max-w-sm">Bitte erlaube den Kamerazugriff fuer die AR-Ansicht.</p>
            </div>
          </div>
        )}

        <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between" style={{ pointerEvents: "auto" }}>
          <div className="glass-surface rounded-lg px-4 py-2 flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${cameraActive ? "bg-[#00F0FF] animate-glow-pulse" : "bg-[#FF3B30]"}`} />
            <span className="text-xs font-['JetBrains_Mono'] text-white">{cameraActive ? "AR-Kamera aktiv" : "Kamera inaktiv"}</span>
          </div>
          <button data-testid="ar-camera-close-btn" onClick={() => { stopCamera(); onClose(); }}
            className="w-10 h-10 flex items-center justify-center rounded-full glass-surface hover:bg-white/10 transition-colors">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6" style={{ pointerEvents: "auto" }}>
          <div className="glass-surface rounded-lg p-4 max-w-lg mx-auto">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-['Outfit'] text-white mb-1">AR-Projektvorschau</p>
                <p className="text-xs text-[#A0A0B0]">{visualElements.length} Elemente werden angezeigt</p>
              </div>
              {cameraActive && (
                <button onClick={() => { stopCamera(); startCamera(); }}
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                  <RotateCcw className="w-4 h-4 text-[#A0A0B0]" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
